import { useEffect, useRef } from 'react'

/**
 * Keeps the screen awake while `active` — the counting screens with an open session.
 *
 * A counter walking the store scans in bursts, and the display sleeping between them costs a
 * re-unlock and a camera restart every time. The lock is released on unmount, so leaving the
 * flow (pause, abort, close) drops it without any extra wiring.
 *
 * iOS releases the sentinel when the tab is backgrounded and does not restore it, so the lock is
 * re-requested on the way back to visible. Unsupported browsers (the Screen Wake Lock API is
 * absent in Safari before 16.4) are a silent no-op: `navigator.wakeLock` is typed as always
 * present but is genuinely undefined there, hence the widened read below.
 */
export function useWakeLock(active: boolean) {
  const sentinelRef = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    if (!active) return
    const wakeLock = (navigator as { wakeLock?: WakeLock }).wakeLock
    if (!wakeLock) return

    let cancelled = false

    function release() {
      const sentinel = sentinelRef.current
      sentinelRef.current = null
      if (sentinel && !sentinel.released)
        void sentinel.release().catch(() => {})
    }

    async function acquire() {
      if (cancelled || sentinelRef.current) return
      try {
        const sentinel = await wakeLock!.request('screen')
        // TS sees no reassignment of `cancelled` between the guard above and here, but the effect
        // cleanup genuinely runs concurrently with the pending request and flips it — that is the
        // whole point of the flag.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (cancelled) {
          void sentinel.release().catch(() => {})
          return
        }
        sentinelRef.current = sentinel
      } catch {
        // Denied, or the document was not visible — nothing to recover, the screen just sleeps.
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      const sentinel = sentinelRef.current
      if (sentinel && !sentinel.released) return
      sentinelRef.current = null
      void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      release()
    }
  }, [active])
}
